import { DEFAULT_WORKING_HOURS_IN_DAY } from '../data/app-parameters'
import { IFormatMessage, IFormatMessageFn, IGetFormattedDaysAndHours, LeaveDisplayFormatEnum } from '../types/display-in-days-hours'
import { IQuotaAmount, QuotaUnits, ShortestLeaveIntervalEnum } from '../types/leave-policy'
import { DayOfWeek, IWorkDay, IWorkingSchedule, WorkingHours } from '../types/work-week'
import { IPartDay } from '../types/leave-request'
import { HourFormatEnum } from '../types/user'
import { roundTo2Decimals, roundTo4Decimals } from './calculation-shared'
import isInteger from 'lodash/isInteger'
import { getAverageWorkingHours, userHasRotatingSchedule, userHasWorkingHoursSchedule } from './work-week'
import { dayNames } from '@vacationtracker/shared/types/calendar'

export interface IComputeDaysAndHours {
  days: number
  hours: number
  minutes: number
  isNegative: boolean
}

// get raw quota in units which are set in general settings (hourlyLeaveAccounting) - ignore units in which the quota was originally set
export function getRawQuotaInSettingsUnit(quota: IQuotaAmount, hourlyLeaveAccounting: boolean): number {
  return hourlyLeaveAccounting ? quota.raw.hours : quota.raw.days
}

// get quota in units which are set in general settings (hourlyLeaveAccounting) - ignore units in which the quota was originally set
// if saved units are diffrent from the units in settings, convert the value by multiplying or dividing by working hours
export function getQuotaInSettingsUnits(
  hourlyLeaveAccounting: boolean,
  workingHours: number,
  inDaysValue: number,
  inHoursValue?: number,
  quotaSetIn?: QuotaUnits
): number {
  if (hourlyLeaveAccounting) {
    return quotaSetIn === 'hours' ? Number(inHoursValue) : roundTo4Decimals(inDaysValue * workingHours)
  }
  return quotaSetIn === 'hours' ? roundTo4Decimals(Number(inHoursValue) / workingHours) : inDaysValue
}

// get quota in units in which it was originally set (either days or hours)
export function getQuotaInOriginalUnits(inDaysValue: number, inHoursValue: number, units: QuotaUnits): number {
  return units === 'hours' ? inHoursValue : inDaysValue
}

// get quota in units in which it is set (either days or hours) if it is set - new companies will have both days, hours and quotaSetIn params set
// old companies will have only inDays values set and we will use hourlyLeaveAccounting to determine the units and convert the hours to days with default working hours
export function getQuotaInUnitsIfSet(inDaysValue: number, inHoursValue: number,  hourlyLeaveAccounting: boolean, quotaSetIn?: QuotaUnits): number {
  if (!quotaSetIn) {
    inHoursValue = inDaysValue * DEFAULT_WORKING_HOURS_IN_DAY
    quotaSetIn = hourlyLeaveAccounting ? 'hours' : 'days'
  }
  return getQuotaInOriginalUnits(inDaysValue, inHoursValue, quotaSetIn)
}

// get quota in days no matter in which units it is set
export function getQuotaInDays(inDaysValue: number, inHoursValue: number, workHours: number, quotaSetIn?: QuotaUnits): number {
  if (quotaSetIn === 'hours' && inHoursValue) {
    return inHoursValue / workHours
  }
  return inDaysValue
}

export function computeDaysAndHoursForLeaveDays(
  leaveDaysWithWorkingHours: IWorkDay[],
  userWorkingHours: WorkingHours | IWorkingSchedule,
  shortestLeaveInterval: ShortestLeaveIntervalEnum
): IComputeDaysAndHours {
  // We show minutes if forceShowMinutes is true or if the number of working hours per day is not an integer (e.g. 7.5, 8.5, 11.5, etc.)
  // If it's an integer (e.g. 8, 9, 10, etc.), we do not show minutes.
  const isShortestLeaveIntervalLessThanOneHour = [
    ShortestLeaveIntervalEnum.halfDay, // can be like 7.5h per day so we need to show minutes because half day is 3h 45m
    ShortestLeaveIntervalEnum.halfHour,
    ShortestLeaveIntervalEnum.fifteenMinutes,
  ].includes(shortestLeaveInterval)
  const hasFloatingWorkingHours =
    (!Array.isArray(userWorkingHours) && userWorkingHours.startDate) ||
    (Array.isArray(userWorkingHours) && userWorkingHours.some(workingHours => !isInteger(workingHours[0])))
  const shouldUse15MinuteIntervals = isShortestLeaveIntervalLessThanOneHour || hasFloatingWorkingHours

  const numberOfMinutesPerHour = 60
  const totalLeaveHours = leaveDaysWithWorkingHours.reduce((acc, workDay) => acc + workDay.leaveHours, 0)
  const isNegative = Math.sign(totalLeaveHours) === -1

  const days = leaveDaysWithWorkingHours.filter(workday => workday.leaveHours === workday.workingHoursInDay).length
  const hoursWithMinutes = leaveDaysWithWorkingHours.filter(workday => workday.leaveHours !== workday.workingHoursInDay).reduce((acc, workday) => acc + workday.leaveHours, 0)
  const hours = Math.floor(hoursWithMinutes)
  const remainingMinutes = (hoursWithMinutes - hours) * numberOfMinutesPerHour
  const roundedMinutes = shouldUse15MinuteIntervals ? (Math.round(remainingMinutes / 15) * 15) : 0

  // If user requests full week with 2 half-day holidays or something similar, we might need to adjust the days and hours
  // We cannot adjust days and hours if user has a schedule with different working hours per day because day is not a fixed hours value
  if (!userHasWorkingHoursSchedule(userWorkingHours) && hours >= getAverageWorkingHours(userWorkingHours)) {
    // Here we are sure that user doesnt have a schedule, so each day has the same working hours, so we take average
    const workingHoursPerDay = getAverageWorkingHours(userWorkingHours)
    return {
      days: days + Math.floor(hours / workingHoursPerDay),
      hours: hours % workingHoursPerDay,
      minutes: roundedMinutes,
      isNegative,
    }
  }

  return {
    days,
    hours,
    minutes: roundedMinutes,
    isNegative,
  }
}

export function computeDaysAndHours(hours: number, numberOfWorkingHoursPerDay: number, showMinutes = false, hasSchedule = false): IComputeDaysAndHours {
  // We show minutes if forceShowMinutes is true or if the number of working hours per day is not an integer (e.g. 7.5, 8.5, 11.5, etc.)
  // If it's an integer (e.g. 8, 9, 10, etc.), we do not show minutes.
  const shouldUse15MinuteIntervals = hasSchedule || showMinutes || !isInteger(numberOfWorkingHoursPerDay)

  const numberOfMinutesPerHour = 60
  const totalMinutes = hours * numberOfMinutesPerHour
  const workingDayMinutes = hasSchedule ? 0 : numberOfWorkingHoursPerDay * numberOfMinutesPerHour
  const isNegative = Math.sign(hours) === -1

  const absMinutes = Math.abs(totalMinutes)
  let days = hasSchedule ? 0 : Math.floor(absMinutes / workingDayMinutes)
  const remainingMinutes = hasSchedule ? absMinutes : absMinutes % workingDayMinutes
  let computedHours = Math.floor(remainingMinutes / numberOfMinutesPerHour)
  let minutes = Math.round(remainingMinutes % numberOfMinutesPerHour)

  if (computedHours === numberOfWorkingHoursPerDay) {
    days += 1
    computedHours = 0
  }

  // Convert 60 minutes to 1 hour BEFORE rounding minutes away
  // This must happen before we set roundedMinutes to 0 (when showMinutes = false)
  // Bug fix: Previously this check only happened after rounding, causing 60 minutes to be lost
  if (minutes === 60) {
    computedHours += 1
    minutes = 0
    // If hours now equal working day length, increment days
    if (computedHours === numberOfWorkingHoursPerDay && !hasSchedule) {
      days += 1
      computedHours = 0
    }
  }

  let roundedMinutes = shouldUse15MinuteIntervals ? (Math.round(minutes / 15) * 15) : 0

  // This check handles the case when rounding to 15-minute intervals produces 60
  if (roundedMinutes === 60) {
    computedHours += 1
    roundedMinutes = 0
    // If hours now equal working day length, increment days
    if (computedHours === numberOfWorkingHoursPerDay && !hasSchedule) {
      days += 1
      computedHours = 0
    }
  }

  return {
    days,
    hours: computedHours,
    minutes: roundedMinutes,
    isNegative,
  }
}

export const getRawDaysOrHours = (quota: IQuotaAmount, units: QuotaUnits, formatMessage: IFormatMessageFn, leaveDisplayFormat = LeaveDisplayFormatEnum.none): string => {
  if (units === 'hours') {
    return String(roundTo2Decimals(quota.raw.hours)) + (leaveDisplayFormat === LeaveDisplayFormatEnum.none
      ? ''
      : ' ' + formatMessage({ id: leaveDisplayFormat === LeaveDisplayFormatEnum.long ? 'dashboard.hours' : 'dashboard.hourSingle' }))
  } else {
    return String(roundTo4Decimals(quota.raw.days)) + (leaveDisplayFormat === LeaveDisplayFormatEnum.none
      ? ''
      : ' ' + formatMessage({ id: leaveDisplayFormat === LeaveDisplayFormatEnum.long ? 'dashboard.days' : 'dashboard.daysSmallCaseSingle' }))
  }
}

export const getFormattedDaysAndHours = ({
  leave,
  formatMessage,
  displayFormat,
}: IGetFormattedDaysAndHours): string => {
  if (leave.days === 0 && leave.hours === 0 && leave.minutes === 0) {
    return displayFormat === 'long'
      ? `0 ${formatMessage({ id: 'dashboard.hours' })}`
      : `0${formatMessage({ id: 'dashboard.hourSingle' })}`
  }

  const daysLong = leave.days !== 0
    ? `${leave.days} ${formatMessage({ id: leave.days === 1 ? 'dashboard.day' : 'dashboard.daysPlural' })}`
    : ''
  const hoursLong = leave.hours !== 0
    ? `${leave.hours} ${formatMessage({ id: leave.hours === 1 ? 'dashboard.hourOne' : 'dashboard.hours' })}`
    : ''
  const minutesLong = leave.minutes !== 0
    ? `${leave.minutes} ${formatMessage({ id: leave.minutes === 1 ? 'dashboard.minute' : 'dashboard.minutes' })}`
    : ''
  const daysShort = leave.days !== 0
    ? `${leave.days}${formatMessage({ id: 'dashboard.daysSmallCaseSingle' })}`
    : ''
  const hoursShort = leave.hours !== 0
    ? `${leave.hours}${formatMessage({ id: 'dashboard.hourSingle' })}`
    : ''
  const minutesShort = leave.minutes !== 0
    ? `${leave.minutes}${formatMessage({ id: 'dashboard.minuteAbbreviation' })}`
    : ''
  const justString = displayFormat === 'long'
    ? `${daysLong} ${hoursLong} ${minutesLong}`
    : `${daysShort} ${hoursShort} ${minutesShort}`

  // This replace removing whitespace from both sides of a string
  return `${leave.isNegative ? '-' : ''}${justString.trim()}`
}

/**
 * This function is used to get the formatted value of the location or user working hours.
 * If user has a schedule, it'll show all days with their working hours, otherwise it'll show the total working hours per day.
 *
 * For example, without schedule, we'll show 8h for 8, or 8h 30m for 8.5.
 * With schedule, we'll show Monday: 8h, Tuesday: 8h, etc.
 *
 * @param formatMessage - The function used to format the message.
 * @param workingHours - The working hours of the user.
 * @returns The formatted working hours.
 *
 * @example
 * ```ts
 * const workingHours = [[1, 8],[2,8],[3,8],[4,8],[5,8]]
 * const workingHoursWithSchedule = [[1, 8.5],[2,7.5],[3,8],[4,12],[5,4]]
 *
 * const formattedWorkhours = getFormattedWorkhours(formatMessage, workingHours)
 * // Returns 8h
 *
 * const formattedWorkhoursWithSchedule = getFormattedWorkhours(formatMessage, workingHoursWithSchedule)
 * // Returns Mon: 8h, Tue: 7h 30m, Wed: 8h, Thu: 12h, Fri: 4h
 * ```
 **/
export const getFormattedWorkhours = (
  formatMessage: IFormatMessageFn,
  userWorkingHours?: WorkingHours|IWorkingSchedule|null ,
  firstDayOfWeek?: number
) => {
  if (userHasRotatingSchedule(userWorkingHours)) {
    return formatMessage({ id: 'user.workHoursRotatingSchedule'}, { totalWeeks: (userWorkingHours as IWorkingSchedule)?.workHours?.length })
  }
  const workingHours = userWorkingHours as WorkingHours
  const hasSchedule = workingHours ? userHasWorkingHoursSchedule(workingHours) : false

  if (firstDayOfWeek === 1 && workingHours && workingHours?.length > 0 && workingHours[0][0] === 0) {
    const sunday = workingHours.shift() as [DayOfWeek, number]
    workingHours.push(sunday)
  }
  // `workingHours` can't be undefined if `hasSchedule` is true, but TypeScript thinks it can be
  if (hasSchedule && workingHours) {
    return workingHours.map(([day, hours]) => {
      const dayName = formatMessage({ id: dayNames[day] }).substring(0, 3)
      return `${dayName}: ${hours} ${formatMessage({ id: 'dashboard.hours' })}`
    }).join(', ')
  }

  const hoursPerDay = workingHours ? getAverageWorkingHours(workingHours) : DEFAULT_WORKING_HOURS_IN_DAY
  const hours = Math.floor(hoursPerDay)
  const minutes = (hoursPerDay - hours) * 60
  return getFormattedDaysAndHours({
    leave: {days: 0, hours, minutes, isNegative: false},
    formatMessage,
    displayFormat: LeaveDisplayFormatEnum.short,
  })
}

export const getRawOrFormattedQuota = (
  quota: IQuotaAmount,
  formatMessage: IFormatMessageFn,
  showRaw: boolean,
  units: QuotaUnits = 'hours',
  displayFormat = LeaveDisplayFormatEnum.short
): string => {
  return showRaw
    ? getRawDaysOrHours(quota, units, formatMessage, displayFormat)
    : getFormattedDaysAndHours({ leave: quota, formatMessage, displayFormat})
}

/**
 * Calculates the number of hours for a part-day.
 *
 * @param partDay - An object representing the part-day with start and end times.
 * @returns The number of hours between the start and end times. Returns 0 if partDay is undefined.
 */
export const calculatePartDayHours = (partDay: IPartDay | undefined): number => {
  if (!partDay) {
    return 0
  }
  const startHoursDecimal = partDay.startHour +
    (partDay.startMinute || 0) / 60
  const endHoursDecimal = partDay.endHour +
    (partDay.endMinute || 0) / 60

  return endHoursDecimal - startHoursDecimal
}

interface ITimeFormatOptions {
  hour: number
  minute: number
  showZeroPadding?: boolean
  hourFormat?: HourFormatEnum
  amOrPm?: string
}

/**
 * Formats a time string based on the provided hour and minute with flexible formatting options.
 *
 * @param hour - The hour to be formatted
 * @param minute - The minute to be formatted
 * @param showZeroPadding - Whether to show :00 for whole hours (default: false) - for calendar params
 * @param hourFormat - '12h' or '24h' format (default: '24h') - Optional - for UI
 * @param amOrPm - The AM/PM indicator (only used with 12h format) - Optional - for UI
 * @returns Formatted time string examples:
 * - 24h UI: "17", "17:15"
 * - 24h Calendar: "17:00", "17:15"
 * - 12h UI: "5PM", "5:15PM"
 */
export const formatTimeString = ({
  hour,
  minute,
  showZeroPadding = false,
  hourFormat,
  amOrPm,
}: ITimeFormatOptions): string => {
  const minuteStr = minute > 0
    ? `:${minute.toString().padStart(2, '0')}`
    : showZeroPadding ? ':00' : ''

  const displayHour = hourFormat === HourFormatEnum.twelve && hour > 12
    ? hour - 12
    : hour

  const paddedHour = showZeroPadding && displayHour < 10
    ? displayHour.toString().padStart(2, '0')
    : displayHour

  const amPmStr = hourFormat === HourFormatEnum.twelve ? (amOrPm || '') : ''

  return `${paddedHour}${minuteStr}${amPmStr}`
}
